import { PlayerState, OrbState } from '@push.io/shared';
import { UpgradeTree } from '@push.io/shared';

export type BotDifficulty = 'easy' | 'medium' | 'hard' | 'insane';

export interface BotProfile {
  // Velocidad de decisión: cada cuántos ticks el bot re-piensa hacia dónde va.
  decisionInterval: number;
  // Tendencia a atacar a un rival próximo (0 = solo pasivo, 1 = muy agresivo).
  aggression: number;
  // Rango al que el bot huye del borde de la arena.
  edgeAwareness: number;
  // Percepción del orbe más cercano (1 = lo ve casi siempre, bajo = errores).
  orbAwareness: number;
  // Probabilidad de comprar una mejora cuando puede permitírsela.
  upgradeBias: number;
  // Orbes que el bot intenta conservar antes de gastar en mejoras.
  orbReserve: number;
}

export const BOT_PROFILES: Record<BotDifficulty, BotProfile> = {
  easy: {
    decisionInterval: 24,
    aggression: 0.18,
    edgeAwareness: 0.25,
    orbAwareness: 0.5,
    upgradeBias: 0.35,
    orbReserve: 4,
  },
  medium: {
    decisionInterval: 14,
    aggression: 0.4,
    edgeAwareness: 0.45,
    orbAwareness: 0.8,
    upgradeBias: 0.6,
    orbReserve: 3,
  },
  hard: {
    decisionInterval: 8,
    aggression: 0.62,
    edgeAwareness: 0.7,
    orbAwareness: 1,
    upgradeBias: 0.85,
    orbReserve: 2,
  },
  insane: {
    decisionInterval: 5,
    aggression: 0.85,
    edgeAwareness: 1,
    orbAwareness: 1,
    upgradeBias: 1,
    orbReserve: 1,
  },
};

export type BotUpgradeRequest = { botId: string; stat: UpgradeTree };

/**
 * IA básica y jugable para los bots. Cada bot recalcula su dirección después de
 * `decisionInterval` ticks y combina tres instintos: recoger orbes, atacar a un
 * rival cercano y evitar el borde de la arena. El comportamiento se ajustará
 * cuando se indique cómo deben trabajar los bots.
 */
export class BotAI {
  private readonly profile: BotProfile;
  private readonly decisionNoise: () => number;
  private readonly enabled: boolean;

  constructor(private readonly difficulty: BotDifficulty) {
    this.profile = BOT_PROFILES[difficulty];
    this.enabled = true;
    this.decisionNoise = () => (difficulty === 'insane' ? 0 : (Math.random() - 0.5));
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getProfile(): BotProfile {
    return this.profile;
  }

  /**
   * Devuelve el vector director (normalizado) hacia el que debe moverse el bot
   * en este tick, dados el estado del mundo y el tick actual.
   */
  decide(
    tick: number,
    bot: PlayerState,
    players: Map<string, PlayerState>,
    orbs: OrbState[],
    arenaRadius: number,
  ): { dirX: number; dirY: number } {
    // El vector deseado actual ya se normaliza aquí para que la física local
    // (que espera dirX/dirY normalizados) empuje correctamente.
    const current = this.normalize(bot.desiredDirX ?? 0, bot.desiredDirY ?? 0);

    if (tick % this.profile.decisionInterval !== 0) return current;

    const [tx, ty] = this.computeDirection(bot, players, orbs, arenaRadius);
    return this.normalize(tx, ty);
  }

  private normalize(x: number, y: number): { dirX: number; dirY: number } {
    const length = Math.hypot(x, y);
    if (length < 1e-6) return { dirX: 0, dirY: 0 };
    return { dirX: x / length, dirY: y / length };
  }

  private computeDirection(
    bot: PlayerState,
    players: Map<string, PlayerState>,
    orbs: OrbState[],
    arenaRadius: number,
  ): [number, number] {
    let tx = 0;
    let ty = 0;

    // Instinto 1: recoger el orbe visible más cercano
    if (Math.random() <= this.profile.orbAwareness) {
      let best: OrbState | null = null;
      let bestDist = Infinity;
      for (const orb of orbs) {
        if (orb.collected) continue;
        const d = (orb.x - bot.x) ** 2 + (orb.y - bot.y) ** 2;
        if (d < bestDist) {
          bestDist = d;
          best = orb;
        }
      }
      if (best) {
        tx += best.x - bot.x;
        ty += best.y - bot.y;
      }
    }

    // Instinto 2: atacar a un rival cercano (más fuerte con la dificultad)
    if (Math.random() <= this.profile.aggression) {
      let rival: PlayerState | null = null;
      let bestDist = Infinity;
      const attackRange = 8 * (0.75 + this.profile.aggression);
      for (const p of players.values()) {
        if (p.id === bot.id || !p.alive) continue;
        const d = (p.x - bot.x) ** 2 + (p.y - bot.y) ** 2;
        if (d < attackRange * attackRange && d < bestDist) {
          bestDist = d;
          rival = p;
        }
      }
      if (rival) {
        tx += (rival.x - bot.x) * 1.4;
        ty += (rival.y - bot.y) * 1.4;
      }
    }

    // Instinto 3: evitar el borde (más importante cuanto menor es la arena)
    const radius = bot.radius ?? 0.5;
    const distFromCenter = Math.hypot(bot.x, bot.y);
    const safeRadius = Math.max(0, arenaRadius - radius);
    const dangerBand = safeRadius * this.profile.edgeAwareness;
    if (distFromCenter > safeRadius - dangerBand && distFromCenter > 1e-6) {
      const inward = (distFromCenter - (safeRadius - dangerBand)) / Math.max(dangerBand, 1e-6);
      tx += (-bot.x / distFromCenter) * inward * 3;
      ty += (-bot.y / distFromCenter) * inward * 3;
    }

    return [tx + this.decisionNoise(), ty + this.decisionNoise()];
  }

  /**
   * Decide si el bot gasta orbes en una mejora en este tick. Devuelve la
   * mejora solicitada o null si no puede/comprar no merece la pena.
   */
  decideUpgrade(bot: PlayerState): UpgradeTree | null {
    if (Math.random() > this.profile.upgradeBias) return null;
    if (bot.orbs < this.profile.orbReserve) return null;

    const order: UpgradeTree[] = [
      UpgradeTree.Push,
      UpgradeTree.Weight,
      UpgradeTree.Speed,
      UpgradeTree.Control,
    ];
    const candidates = order.filter((stat) => bot.upgrades[stat] < 4);
    if (candidates.length === 0) return null;

    // Dificultad fácil/média prefiere velocidad y control; agresivas, empuje y peso.
    const preferred = this.difficulty === 'easy' || this.difficulty === 'medium'
      ? candidates.filter((stat) => stat === UpgradeTree.Speed || stat === UpgradeTree.Control)
      : candidates.filter((stat) => stat === UpgradeTree.Push || stat === UpgradeTree.Weight);
    const pool = preferred.length > 0 ? preferred : candidates;
    return pool[Math.floor(Math.random() * pool.length)];
  }
}